import React from 'react';

import type { UnsplashImageSelection } from '../components/UnsplashImageLibraryDialog';
import {
  buildUnsplashCredit,
  generateAltText,
  getOptimizedImageUrl,
  hasUnsplashAccessKey,
  searchUnsplashPhotos,
  type UnsplashPhoto,
} from '../services/unsplashService';
import {
  buildGlobalSymbolCredit,
  generateGlobalSymbolAltText,
  getGlobalSymbolImageUrl,
  searchGlobalSymbolsLabels,
  type GlobalSymbolsLabel,
} from '../services/globalSymbolsService';

type PendingImageProvider = 'unsplash' | 'globalsymbols';

export interface PendingImageQuickPickCandidate {
  id: string;
  provider: PendingImageProvider;
  providerLabel: string;
  previewSrc: string;
  title: string;
  subtitle: string;
  selection: UnsplashImageSelection;
}

interface ProviderState {
  items: PendingImageQuickPickCandidate[];
  error: string | null;
}

interface CachedProviderState extends ProviderState {
  loaded: boolean;
  promise: Promise<ProviderState> | null;
}

export interface PendingImageQuickPickState {
  query: string;
  photos: PendingImageQuickPickCandidate[];
  symbols: PendingImageQuickPickCandidate[];
  photosLoading: boolean;
  symbolsLoading: boolean;
  photoError: string | null;
  symbolError: string | null;
}

const EMPTY_PROVIDER_STATE: ProviderState = {
  items: [],
  error: null,
};

const MAX_CACHE_SIZE = 50;

const unsplashCache = new Map<string, CachedProviderState>();
const globalSymbolsCache = new Map<string, CachedProviderState>();

function setWithEviction<V>(map: Map<string, V>, key: string, value: V) {
  if (map.size >= MAX_CACHE_SIZE && !map.has(key)) {
    const firstKey = map.keys().next().value;
    if (firstKey !== undefined) map.delete(firstKey);
  }
  map.set(key, value);
}

function readCache(map: Map<string, CachedProviderState>, key: string): CachedProviderState | null {
  return map.get(key) ?? null;
}

function buildUnsplashCandidates(
  query: string,
  photos: UnsplashPhoto[]
): PendingImageQuickPickCandidate[] {
  return photos
    .slice(0, 2)
    .map((photo) => {
      const previewSrc =
        photo.urls?.small || photo.urls?.thumb || getOptimizedImageUrl(photo, 240, 160, 'crop');
      const resolvedSrc = getOptimizedImageUrl(photo, 1000, 700, 'crop');
      const alt = generateAltText(photo, query);
      const title = buildUnsplashCredit(photo);

      return {
        id: `unsplash:${photo.id}`,
        provider: 'unsplash' as const,
        providerLabel: 'Photo',
        previewSrc,
        title: alt,
        subtitle: photo.user?.name?.trim() || 'Unsplash',
        selection: {
          src: resolvedSrc,
          alt,
          title,
        },
      };
    })
    .filter((candidate) => candidate.previewSrc.length > 0 && candidate.selection.src.length > 0);
}

function buildGlobalSymbolCandidates(
  query: string,
  labels: GlobalSymbolsLabel[]
): PendingImageQuickPickCandidate[] {
  return labels
    .map((label) => {
      const previewSrc = getGlobalSymbolImageUrl(label);
      const alt = generateGlobalSymbolAltText(label, query);
      const title = buildGlobalSymbolCredit(label);

      return {
        id: `globalsymbols:${label.id}`,
        provider: 'globalsymbols' as const,
        providerLabel: 'Symbol',
        previewSrc,
        title: alt,
        subtitle: label.picto?.symbolset?.name?.trim() || 'Global Symbols',
        selection: {
          src: previewSrc,
          alt,
          title,
        },
      };
    })
    .filter((candidate) => candidate.previewSrc.length > 0)
    .slice(0, 2);
}

async function ensureUnsplashState(query: string): Promise<ProviderState> {
  const normalized = query.trim().toLowerCase();
  if (!normalized || !hasUnsplashAccessKey()) {
    return EMPTY_PROVIDER_STATE;
  }

  const cached = unsplashCache.get(normalized);
  if (cached?.promise) return cached.promise;
  if (cached?.loaded) return cached;

  const promise = searchUnsplashPhotos(query, 1, 4)
    .then((photos) => {
      const nextState = {
        items: buildUnsplashCandidates(query, photos),
        error: null,
      };
      setWithEviction(unsplashCache, normalized, { ...nextState, loaded: true, promise: null });
      return nextState;
    })
    .catch((error) => {
      const nextState = {
        items: [],
        error: error instanceof Error ? error.message : 'Unable to load photo suggestions.',
      };
      setWithEviction(unsplashCache, normalized, { ...nextState, loaded: true, promise: null });
      return nextState;
    });

  setWithEviction(unsplashCache, normalized, {
    items: cached?.items ?? [],
    error: cached?.error ?? null,
    loaded: cached?.loaded ?? false,
    promise,
  });

  return promise;
}

async function ensureGlobalSymbolsState(query: string): Promise<ProviderState> {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return EMPTY_PROVIDER_STATE;
  }

  const cached = globalSymbolsCache.get(normalized);
  if (cached?.promise) return cached.promise;
  if (cached?.loaded) return cached;

  const promise = searchGlobalSymbolsLabels(query, {
    language: 'eng',
    limit: 12,
  })
    .then((labels) => {
      const nextState = {
        items: buildGlobalSymbolCandidates(query, labels),
        error: null,
      };
      setWithEviction(globalSymbolsCache, normalized, {
        ...nextState,
        loaded: true,
        promise: null,
      });
      return nextState;
    })
    .catch((error) => {
      const nextState = {
        items: [],
        error: error instanceof Error ? error.message : 'Unable to load symbol suggestions.',
      };
      setWithEviction(globalSymbolsCache, normalized, {
        ...nextState,
        loaded: true,
        promise: null,
      });
      return nextState;
    });

  setWithEviction(globalSymbolsCache, normalized, {
    items: cached?.items ?? [],
    error: cached?.error ?? null,
    loaded: cached?.loaded ?? false,
    promise,
  });

  return promise;
}

function readQuickPickState(query: string): PendingImageQuickPickState {
  const normalized = query.trim().toLowerCase();

  if (!normalized) {
    return {
      query,
      photos: [],
      symbols: [],
      photosLoading: false,
      symbolsLoading: false,
      photoError: null,
      symbolError: null,
    };
  }

  const unsplashState = readCache(unsplashCache, normalized);
  const globalSymbolsState = readCache(globalSymbolsCache, normalized);

  return {
    query,
    photos: unsplashState?.items ?? [],
    symbols: globalSymbolsState?.items ?? [],
    photosLoading: Boolean(unsplashState?.promise),
    symbolsLoading: Boolean(globalSymbolsState?.promise),
    photoError: unsplashState?.error ?? null,
    symbolError: globalSymbolsState?.error ?? null,
  };
}

export function usePendingImageQuickPicks(
  query: string,
  enabled = true
): PendingImageQuickPickState {
  const normalizedQuery = query.trim();
  const [state, setState] = React.useState<PendingImageQuickPickState>(() =>
    readQuickPickState(normalizedQuery)
  );

  React.useEffect(() => {
    if (!enabled) {
      setState(readQuickPickState(normalizedQuery));
      return;
    }

    if (!normalizedQuery) {
      setState(readQuickPickState(normalizedQuery));
      return;
    }

    let cancelled = false;

    const syncState = () => {
      if (!cancelled) {
        setState(readQuickPickState(normalizedQuery));
      }
    };

    syncState();

    void Promise.all([
      ensureUnsplashState(normalizedQuery),
      ensureGlobalSymbolsState(normalizedQuery),
    ]).then(syncState);

    return () => {
      cancelled = true;
    };
  }, [enabled, normalizedQuery]);

  return state;
}
